import { Spacing } from '@persona-id/design-tokens';
import styled from 'styled-components';

import AttributeEditorTable from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/Panels/AttributeEditorTable';
import {
  VERIFICATION_GOVERNMENT_ID_ATTRIBUTES,
  VERIFICATION_SELFIE_ATTRIBUTES,
} from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/Panels/ResourceVerification/constants';
import ReactMultiSelect from 'dashboard/components/ReactMultiSelect';
import { generatePlaceHolderDisplay } from 'dashboard/components/ReactMultiSelect/ReactMultiSelect';
import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import { Verification } from 'dashboard/models/verification';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import { ModuleResourceVerification } from 'dashboard/modules/ResourceVerification';
import Spacer from 'design-system/components/Spacer';
import { Heading, Label } from 'design-system/components/Text';
import { ApiModel } from 'lib/interfaces/api';

const Container = styled('div')`
  padding: ${Spacing[5]};
`;

export const VERIFICATION_TYPE_OPTIONS: OptionType<Verification['type']>[] = [
  {
    label: 'AAMVA',
    value: ApiModel.VerificationAamva,
  },
  {
    label: 'Business Website',
    value: ApiModel.VerificationBusinessWebsite,
  },
  {
    label: 'Database',
    value: ApiModel.VerificationDatabase,
  },
  {
    label: 'Database (Aadhaar)',
    value: ApiModel.VerificationDatabaseAadhaar,
  },
  {
    label: 'Database (Business Registry)',
    value: ApiModel.VerificationDatabaseBusiness,
  },
  {
    label: 'Database (Business Footprint)',
    value: ApiModel.VerificationDatabaseBusinessFootprint,
  },
  {
    label: 'Database (eCBSV)',
    value: ApiModel.VerificationDatabaseEcbsv,
  },
  {
    label: 'Database (Phone Carrier)',
    value: ApiModel.VerificationDatabasePhoneCarrier,
  },
  {
    label: 'Database (Standard)',
    value: ApiModel.VerificationDatabaseStandard,
  },
  {
    label: 'Database (TIN)',
    value: ApiModel.VerificationDatabaseTin,
  },
  {
    label: 'Database (VAT Number)',
    value: ApiModel.VerificationDatabaseVatNumber,
  },
  {
    label: 'Digital ID UK Sharecode',
    value: ApiModel.VerificationDigitalIdUkSharecode,
  },
  {
    label: 'Document',
    value: ApiModel.VerificationDocument,
  },
  {
    label: 'Email Address',
    value: ApiModel.VerificationEmailAddress,
  },
  {
    label: 'Government ID',
    value: ApiModel.VerificationGovernmentId,
  },
  {
    label: 'Phone Number',
    value: ApiModel.VerificationPhoneNumber,
  },
  {
    label: 'Selfie',
    value: ApiModel.VerificationSelfie,
  },
];

const getVerificationAttributes = (verificationType: Verification['type']) => {
  switch (verificationType) {
    case ApiModel.VerificationGovernmentId:
      return VERIFICATION_GOVERNMENT_ID_ATTRIBUTES;
    case ApiModel.VerificationSelfie:
      return VERIFICATION_SELFIE_ATTRIBUTES;
    default:
      return [];
  }
};

export interface ResourceVerificationPanelProps {
  module: ModuleResourceVerification;
  publishedVerificationTemplates: VerificationTemplate[];
  updateModule: (module: ModuleResourceVerification) => void;
}

const ResourceVerificationPanel = ({
  module,
  publishedVerificationTemplates,
  updateModule,
}: ResourceVerificationPanelProps) => {
  const updateAttributes = (attributes: string[]) => {
    updateModule({
      ...module,
      attributes,
    });
  };

  const handleSetVerificationTemplates = (
    verificationTemplates: ModuleResourceVerification['verification-templates'],
  ) => {
    updateModule({
      ...module,
      'verification-templates': verificationTemplates,
    });
  };

  const handleSetVerificationType = (
    verificationType: ModuleResourceVerification['verification-type'],
  ) => {
    updateModule({
      ...module,
      'verification-type': verificationType,
    });
  };

  const verificationTemplateOptions = publishedVerificationTemplates
    .map((verificationTemplate) => {
      return {
        label: verificationTemplate.attributes.name,
        value: verificationTemplate.id,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
  return (
    <>
      <Container>
        <Heading size='sm'>Settings</Heading>
        <Spacer height={2} />
        <Label id='verification-templates-selector-label' size='md' weight='bold'>
          Verification templates
        </Label>
        <ReactMultiSelect
          aria-labelledby='verification-templates-selector-label'
          aria-describedby='verification-templates-selector-label'
          options={verificationTemplateOptions}
          value={module['verification-templates'] || []}
          onChange={handleSetVerificationTemplates}
          placeholderGenerator={generatePlaceHolderDisplay({
            label: { single: 'Verification Template', multiple: 'Verification Templates' },
            emptyIsAll: true,
          })}
          selectAllEnabled
          searchEnabled
          portal
        />
        <Spacer height={2} />
        <Label id='verification-type-selector-label' size='md' weight='bold'>
          Type
        </Label>
        <ReactSelect
          options={VERIFICATION_TYPE_OPTIONS}
          value={module['verification-type']}
          onChange={handleSetVerificationType}
          aria-labelledby='verification-type-selector-label'
          portal
        />
      </Container>
      {getVerificationAttributes(module['verification-type']).length > 0 && (
        <AttributeEditorTable
          allAttributes={getVerificationAttributes(module['verification-type'])}
          moduleAttributes={module.attributes}
          updateAttributes={updateAttributes}
        />
      )}
    </>
  );
};

export default ResourceVerificationPanel;
